import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useRouting } from 'hooks/useRouting';
import { formatDateForString } from 'utils/formatter';
import { Mode } from './TargetUpdateStep1';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { Target, TargetType } from '../utils/type';
import { useBirthday } from '../utils/birthday';
import { targetTypeToImg, targetTypeToTitle } from '../utils/targetInfo';
import { TargetValidateError, validateTarget } from '../utils/validate';
import { dateToString } from '../utils/utils';
import { InputRow } from '../components/commons/InputRow';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { SetPropertiesBase } from '../components/features/share/SetPropertiesBase';
import { useTargetTitleForm } from '../hooks/useTargetTitleForm';
import { useTargetAmountForm } from '../hooks/useTargetAmountForm';
import { useTargetAgeOrYearForm } from '../hooks/useTargetAgeOrYear';

const TargetUpdateInfoInternal = ({
  id,
  mode,
  birthday,
  confirmGlobalTarget,
  updateConfirmGlobalTarget,
}: {
  id: string;
  mode: Mode;
  birthday: Date;
  confirmGlobalTarget: Target;
  updateConfirmGlobalTarget: (data: Target) => void;
}) => {
  const routing = useRouting();
  const { goTargetReplaceUpdateConfirm } = useTargetSupportRouting();
  const targetType = Number(confirmGlobalTarget.refType) as TargetType;

  const [validate, setValidate] = useState<
    { tag: 'Success' } | { tag: 'Error'; errors: TargetValidateError[] }
  >({ tag: 'Success' });

  const titleErrors = useMemo(() => {
    return validate.tag === 'Success'
      ? []
      : validate.errors.flatMap(e => (e.tag === 'TitleError' ? [e.error] : []));
  }, [validate]);

  const { TargetTitleForm, targetTitle } = useTargetTitleForm(
    titleErrors,
    confirmGlobalTarget.title
  );

  const amountErrors = useMemo(() => {
    return validate.tag === 'Success'
      ? []
      : validate.errors.flatMap(e =>
          e.tag === 'AmountError' ? [e.error] : []
        );
  }, [validate]);

  const { TargetAmountForm, targetAmount } = useTargetAmountForm({
    amountErrors,
    initialTargetAmount: confirmGlobalTarget.targetAmount,
  });

  const dateErrors = useMemo(() => {
    return validate.tag === 'Success'
      ? []
      : validate.errors.flatMap(e => (e.tag === 'DateError' ? [e.error] : []));
  }, [validate]);

  const { TargetAgeOrYearForm, absYear, Switch } = useTargetAgeOrYearForm({
    birthday,
    dateErrors,
    initialValueYear: Number(
      formatDateForString(confirmGlobalTarget.endDate, 'yyyy')
    ),
  });

  const targetValidate = useMemo(
    () =>
      validateTarget({
        targetType,
        targetTitle,
        targetAmount,
        assetAmount: confirmGlobalTarget.targetAmount,
        targetYear: absYear,
        achieved: false,
        thisYear: new Date().getFullYear(),
        birthYear: birthday.getFullYear(),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [absYear, targetAmount, targetTitle]
  );

  useEffect(() => {
    const validate_ =
      targetValidate.tag === 'Success'
        ? { tag: targetValidate.tag }
        : { tag: targetValidate.tag, errors: targetValidate.errors };
    setValidate(validate_);
  }, [targetValidate]);

  const onFinish = () => {
    const updateTarget = {
      ...confirmGlobalTarget,
      title: targetTitle,
      targetAmount: targetAmount ?? 0,
      endDate: dateToString(new Date(absYear, 1, 1)),
    };

    updateConfirmGlobalTarget(updateTarget);
    goTargetReplaceUpdateConfirm(id, mode);
  };

  return (
    <>
      <SetPropertiesBase
        {...{
          finishButton: {
            message: <>変更する</>,
            isDisabled: targetValidate.tag === 'Error',
          },
          cancelButtonStr: '戻る',
          cancelButtonClassName:
            'text-bluegray-700 font-bold border border-bluegray-400 rounded-lg shadow',
          onFinish: () => onFinish(),
          onCancel: () => routing.backRouting(true),
          imgPath: targetTypeToImg(confirmGlobalTarget.refType),
          imgOverStr: targetTypeToTitle(confirmGlobalTarget.refType),
        }}>
        <InputRow {...{ title: 'タイトル' }} className={`mb-8`}>
          {TargetTitleForm}
        </InputRow>
        <InputRow {...{ title: '目標金額（単位：万円）' }} className={`mb-8`}>
          {TargetAmountForm}
        </InputRow>
        <InputRow
          {...{
            title: '達成時期',
            SubInformation: Switch,
          }}
          className={``}>
          {TargetAgeOrYearForm}
        </InputRow>
      </SetPropertiesBase>
    </>
  );
};

const TargetUpdateInfo = (): React.ReactElement => {
  useCustomHeaderTitle('目標の変更');
  const { id, mode } = useParams<{ id: string; mode: Mode }>();
  const { birthday } = useBirthday();
  const {
    confirmGlobalTarget,
    updateConfirmGlobalTarget,
  } = useTargetAchievementSupportContext();

  if (!confirmGlobalTarget || id !== confirmGlobalTarget.id) {
    return <Redirect to={`/target/${id}`} />;
  }

  if (birthday === null || birthday === undefined) {
    return (
      <PremiumCheck content={<TargetPremium />}>
        <NotFoundBirthday />
      </PremiumCheck>
    );
  }

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetUpdateInfoInternal
        id={id}
        mode={mode}
        birthday={birthday}
        confirmGlobalTarget={confirmGlobalTarget}
        updateConfirmGlobalTarget={updateConfirmGlobalTarget}
      />
    </PremiumCheck>
  );
};

export default TargetUpdateInfo;
